<template>
    <div v-if="member.contact"
         class="member-tile"
         :class="{'selected': selectedMember === member}">
        <div class="member-tile-photo"
             @click="handleEditClick">
            <ContactPhoto :contact="member.contact" />

            <span v-if="isPrimaryMember"
                  class="member-label">
                {{ l('MEMBERS_AND_ADDRESS.PRIMARY_MEMBER') }}
            </span>

            <button v-if="showEditBtn"
                    class="btn member-edit-btn">
                <i class="fa-solid fa-pen-to-square"></i>
            </button>

            <button v-if="!member.isSaved() && !member.isSaving()"
                    class="btn unsaved-member-btn"
                    @click.stop="saveMember">
                <i class="fa-regular fa-floppy-disk-circle-xmark"></i>
            </button>

            <button v-if="member.isSaving()"
                    class="btn unsaved-member-btn">
                <i class="fa-solid fa-spinner fa-spin"></i>
            </button>
        </div>

        <div class="member-tile-footer">
            <div class="member-tile__info user-selectable">
                <p class="member-tile__name"
                   :class="{
                       'member-tile__name--long': memberNameLength > 20,
                       'member-tile__name--very-long': memberNameLength > 29
                   }">
                    <i class="fa-solid fa-user fa-fw"></i>
                    <ContactNameWithTitle :contact="member.contact" />
                </p>

                <p v-if="shouldShowMemberRef">
                    <i class="fa-solid fa-barcode fa-fw"></i>{{ member.contact.ref }}
                    <span v-if="showMembershipRef && membership">({{ membership.ref }})</span>
                </p>

                <p>
                    <i class="fa-solid fa-calendar fa-fw"></i>{{ member.contact.getPoliteAge() }}
                </p>
            </div>
        </div>

        <div v-if="membershipSubscription"
             class="member-tile-actions">
            <MemberRedemptionButton :member="member"
                                    :membership="membership"
                                    @toggle-visit="handleToggleVisit" />
        </div>
    </div>
</template>

<script>
import ContactNameWithTitle from '@/epos/vue/components/Contacts/ContactNameWithTitle';
import ContactPhoto from '@/epos/vue/components/Customers/ContactPhoto';
import MemberRedemptionButton from '@/epos/vue/components/Memberships/MemberRedemptionButton';
import MembershipCardAllocationType from '@/lib/Memberships/MembershipCardAllocationType';
import { container } from '@/container';
import { timeoutPromise } from '@/functions/functions';

export default {
    components: {
        MemberRedemptionButton,
        ContactNameWithTitle,
        ContactPhoto,
    },

    props: {
        /**
         * @type {Member}
         */
        member: {
            type: Object,
            required: true,
        },

        /**
         * @type {Membership}
         */
        membership: {
            type: Object,
            required: true,
        },

        /**
         * @type {MembershipSubscription}
         */
        membershipSubscription: {
            type: Object,
            required: false,
            default: null,
        },

        /**
         * @type {Member}
         */
        selectedMember: {
            type: Object,
            required: false,
            default: null,
        },

        showEditBtn: {
            type: Boolean,
            default: false,
        },

        /**
         * Force showing the membership ref
         */
        showMembershipRef: {
            type: Boolean,
            default: false,
        },

        /**
         * Member index (0-based)
         */
        index: {
            type: Number,
            default: null,
        },

    },

    emits: ['edit-member', 'toggle-visit'],

    computed: {
        isPrimaryMember() {
            // Show primary label if index is 0 (not null).
            return this.index === 0;
        },

        memberNameLength() {
            if (!this.member.contact) {
                return 0;
            }
            return this.member.contact.getFullName().length;
        },

        shouldShowMemberRef() {
            const showMemberRefsFor = [
                MembershipCardAllocationType.ADULTS,
                MembershipCardAllocationType.MEMBER,
            ];

            const shouldShow = showMemberRefsFor.indexOf(container.userService.company.membershipCardAllocation) !== -1;

            if (this.showMembershipRef) {
                return true;
            }

            return shouldShow && this.member.contact && this.member.contact.ref;
        },
    },

    methods: {
        handleEditClick() {
            this.$emit(
                'edit-member',
                this.member,
            );
        },

        handleToggleVisit() {
            this.$emit(
                'toggle-visit',
                this.member,
                this.membershipSubscription,
                this.membershipSubscription.membershipPlan,
                this.membership
            );
        },

        async saveMember($event) {
            if ($event) {
                $event.stopPropagation();
            }

            this.member.saving = true;

            // Add a small delay to show spinner
            await timeoutPromise(500);

            this.member.saving = false;

            if (!this.member.contact.firstName || !this.member.contact.lastName) {
                container.toastFactory.warningTop('MEMBERSHIP_VALIDATION.NO_FIRST_LAST_NAME');
                return;
            }

            this.member.saved = true;
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";

.member-tile {
    display: flex;
    min-height: 235px;
    flex: 0 0 199px; // 199px allows 3 members to show with a scrollbar at 1024x768
    align-self: stretch;
    background: @white;
    flex-direction: column;
    width: 100%;

    .contact-photo {
        width: 100%;
        height: 199px; // 200px caused a small gap.
    }

    &__info {
        display: flex;
        flex-direction: column;
        gap: 5px;
        color: @dt-dark-grey;
        font-size: 13px;

        i {
            color: @dt-dark-blue;
            font-size: 12px;
            margin-right: 5px;
            vertical-align: middle;
            position: relative;
            top: -2px;
        }

        p {
            margin: 0;
        }
    }

    &__name {
        font-size: 18px;

        &.member-tile__name--long {
            font-size: @font-size-medium;
        }

        &.member-tile__name--very-long {
            font-size: 11px;
        }
    }

    &.selected {
        opacity: 1 !important;
    }
}

@media (max-width: @screen-xs-max) {
    .member-tile {
        margin: 0 0 @gutter-width-mobile !important;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
        border-radius: @border-radius-base;

        .member-tile-footer {
            background: none;
        }
    }
}
</style>
